import { useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DownloadIcon,
  FileTextIcon,
  InfoIcon,
  OctagonAlertIcon,
  PackageCheckIcon,
  PencilIcon,
  PrinterIcon,
  Share2Icon,
  TriangleAlertIcon,
} from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import {
  COPY_TYPES,
  panFromGstin,
  type ChallanStatus,
  type ChallanType,
  type CopyType,
} from "@steelbill/types"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"
import { toast } from "sonner"
import { DateField } from "@/components/DateField"
import { SyncStatus } from "@/components/SyncStatus"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/features/auth/auth-context"
import {
  useCancelChallan,
  useChallan,
  useChallanPdf,
  useMarkChallanReturned,
} from "@/features/challans/use-challan"
import { DocumentStatusBadge } from "@/features/invoices/DocumentStatusBadge"
import { useCompany } from "@/features/settings/use-company"
import { requestBlob } from "@/lib/api"
import { ApiError } from "@/lib/api-error"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"
import { endpoints } from "@/lib/endpoints"
import type {
  ChallanDetail,
  DocumentCustomerSnapshot,
  DocumentLine,
} from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { shareDocument } from "@/lib/share"
import { routes } from "@/routes/paths"

/**
 * One delivery challan (§17.7, Rule 55 CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * HOW THIS DIFFERS FROM THE BILL AND QUOTATION SCREENS, AND WHY.
 *
 * IT NAMES BOTH PARTIES, AND WHICH IS WHICH DEPENDS ON THE DOCUMENT. Rule 55(1)
 * requires the consigner AND the consignee on the challan. On `JOB_WORK_IN` the
 * CUSTOMER is the consigner and this business is the consignee; on every other
 * type it is the other way round. That swap is the ONE thing on this page that
 * is simply WRONG rather than untidy if it is inverted — printing the receiver
 * as the sender of goods already on a lorry is a false document. So the screen
 * reads {@link ChallanDetail.direction}, which the SERVER derives, and never
 * re-derives it from `challanType`: a second copy of that rule is precisely the
 * thing that could disagree with the first (`challanDirection` in enums.ts says
 * so in as many words).
 *
 * IT IS NOT A TAX INVOICE, AND THE FIGURES SAY SO. A challan carries a taxable
 * value because Rule 55(1) demands one — and a rate and amount where the movement
 * is for supply — so the lines run through the same engine an invoice's do. None
 * of it reaches GSTR-3B and none of it is money anybody pays. Only the SERIAL
 * RANGE is declared, in GSTR-1 (§17.4), which is why cancelling keeps the number.
 * The totals card therefore never says "grand total", "payable" or "GST due"; it
 * says DECLARED VALUE OF THE GOODS, and the tax rows carry the caption that they
 * are shown, not owed.
 *
 * IT PRINTS THREE COPIES, AND THE MIDDLE ONE IS THE POINT. Rule 55(2) requires
 * copies marked for the consignee, the transporter and the consigner — printed
 * as `(Original for Consignee)`, `(Duplicate for Transporter)` and
 * `(Triplicate for Consigner)` — and the DUPLICATE is the copy the DRIVER must
 * legally be carrying. This screen
 * said "there is no PDF, and its absence is deliberate" until 2026-08-16 — true
 * when written, and stale for as long as it took anyone to notice that the route
 * had landed in `apps/api/src/routes/documents-pdf.ts` rather than in
 * `routes/challans.ts`, where every reader went looking. Until then there was no
 * way at all to print the copy the law puts in the lorry.
 *
 * THERE IS NO "MAKE A COPY", AND THAT IS THE ONE ACTION THE BILL HAS AND THIS
 * SCREEN DOES NOT. `routes/paths.ts` declares no `challanCopy` on purpose: every
 * movement is its own event with its own vehicle and its own date, and a copied
 * challan would carry last week's lorry number onto today's gate.
 *
 * IT PRINTS THE CANCELLATION REASON ON THE DOCUMENT. `audit_log` is an ADMIN
 * surface (`audit.view`, §2.1) that the OWNER cannot open. If he is ever to be
 * told why the challan in front of him was cancelled, it has to be here.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * EVERY FIGURE COMES FROM THE SERVER (§6). The only arithmetic in this file is
 * `qtyMilli / 1000` and `gstRateBp / 100`, both for display, both commented.
 */

/* -------------------------------------------------------------------------- */
/* The action row                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The navigation-menu triggers, dressed as outline Buttons.
 *
 * COPIED VERBATIM FROM `InvoiceDetailScreen`, deliberately and with the same
 * value. Left alone the stock trigger is `h-9` with no border and no `gap`, so it
 * is simultaneously TALLER than the `h-8` Button beside it and lighter than it —
 * which reads as smaller, because weight carries more than height in a row. The
 * missing `gap` is what ran the printer icon into "Print a copy".
 *
 * Matched against `buttonVariants({ variant: "outline" })` field for field.
 */
const TRIGGER_AS_BUTTON =
  "h-8 gap-1.5 rounded-lg border border-border bg-background px-2.5 hover:bg-muted hover:text-foreground data-popup-open:bg-muted data-open:bg-muted"

/**
 * Rule 55(2)'s three copies, and the key each is offered under.
 *
 * THE LABELS ARE THE BILL'S OWN, NOT NEW CHALLAN-SPECIFIC ONES. `doc.originalCopy`
 * / `doc.duplicateCopy` / `doc.triplicateCopy` already read as the owner's words —
 * "Customer copy", "Transporter copy", "My file copy" — and they are true of a
 * challan unchanged. Inventing a parallel set would be two wordings of one idea,
 * to be corrected separately the first time either is improved.
 *
 * THE CAPTIONS THEY PRINT ARE THE SERVER'S AND ARE NOT THESE. §17.7 gives a
 * challan `(Original for Consignee)` / `(Duplicate for Transporter)` /
 * `(Triplicate for Consigner)`. The FORM matches the invoice's since 2026-08-16;
 * the WORDS still differ, because Rule 55 names a consignee and a consigner
 * where §9.2 names a recipient and a supplier. `CHALLAN_COPY_TYPE_CAPTIONS` in
 * `services/pdf/challan-pdf.ts` owns them.
 */
const COPY_LABEL_KEY: Record<CopyType, string> = {
  ORIGINAL: "doc.originalCopy",
  DUPLICATE: "doc.duplicateCopy",
  TRIPLICATE: "doc.triplicateCopy",
}

/*
 * ORDERED BY `COPY_TYPES`, THE SHARED ENUM, RATHER THAN BY A LIST WRITTEN OUT
 * HERE. `Record<CopyType, string>` above makes a missing entry a compile error,
 * and mapping the enum makes a missing MENU ITEM impossible — a challan printed
 * in duplicate rather than triplicate is short of a copy the law requires, and a
 * hand-written array is exactly where that goes unnoticed.
 */
const CHALLAN_COPIES: readonly CopyType[] = COPY_TYPES

/* -------------------------------------------------------------------------- */
/* Status, in words                                                            */
/* -------------------------------------------------------------------------- */

/*
 * THERE IS NO `ChallanStatusBadge` HERE ANY MORE — deleted 2026-08-16, and the
 * deletion is the point.
 *
 * This file carried a fork: a private `STATUS_TONE` map with its own copy of the
 * four tone class-strings, and a comment explaining that `DocumentStatusBadge`
 * could not be used because its `StatusDocumentKind` was `"invoice" |
 * "quotation"` and its `STATUS` table had no OPEN / RETURNED / INVOICED. That
 * was true when written. It stopped being true when the shared component was
 * widened — it now has all three statuses, `"challan"` in the union, and a
 * deliberate empty `challan: {}` override — and the fork was never removed.
 *
 * A duplicated colour table is the quietest kind of wrong: nothing looks broken
 * until someone retunes amber, and then the register and this screen show the
 * same challan in two different ambers. The fork's own comment said the fold
 * would be a delete and kept its translation keys identical so that it would be.
 * It was.
 */

/* -------------------------------------------------------------------------- */
/* The reason for the movement, in words                                       */
/* -------------------------------------------------------------------------- */

/**
 * WHY the goods moved — the legal basis under Rule 55(1), not a category.
 *
 * `Record<ChallanType, string>` on purpose: adding a value to `CHALLAN_TYPES`
 * fails this file to compile rather than silently rendering `JOB_WORK_OUT` on a
 * document. The wording is the owner's, not the enum's.
 */
const TYPE_LABEL_KEY: Record<ChallanType, string> = {
  JOB_WORK_OUT: "challan.typeJobWorkOut",
  JOB_WORK_IN: "challan.typeJobWorkIn",
  SUPPLY_NOT_INVOICED: "challan.typeSupplyNotInvoiced",
  APPROVAL: "challan.typeApproval",
  RETURNABLE: "challan.typeReturnable",
  OTHER: "challan.typeOther",
}

/* -------------------------------------------------------------------------- */
/* What may still be done to it                                                */
/* -------------------------------------------------------------------------- */

/**
 * The statuses `POST /:id/cancel` still accepts — `challanCancelRefusal`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * RETURNED IS IN THE LIST, AND `CLOSED_CHALLAN_STATUSES` IS NOT THE RIGHT TEST
 * HERE. That constant answers "may this be EDITED or advanced?", and a returned
 * challan may not. Cancelling is a different question, and the service answers it
 * differently on purpose: "a returned challan may still be cancelled — 'these
 * goods went out and came back on the wrong paperwork' is an ordinary
 * correction, and the record survives with its reason either way."
 *
 * Only INVOICED and CANCELLED are refused: a tax invoice depends on the movement
 * and that invoice cannot be edited (§17.3), and cancelling twice is nothing.
 *
 * The server refuses these regardless — this decides what is OFFERED, never what
 * is allowed. And it HIDES rather than disables, which is the owner's standing
 * correction: a greyed-out Cancel invites hunting for the way to un-grey it, and
 * the honest answer on a billed challan — you cannot, raise a credit note — is
 * better said by the control not being there and the banner saying so.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const CANCELLABLE: ReadonlySet<string> = new Set<ChallanStatus>(["OPEN", "RETURNED"])

/**
 * The banner a closed challan carries, keyed by status.
 *
 * ONE ENTRY PER TERMINAL STATE, EACH WORDED FOR ITS OWN REASON. A single "this
 * challan is closed" would be true and useless: the owner's next step differs
 * completely between "the goods came back" (nothing), "it has been billed" (the
 * bill is the document now) and "it was cancelled" (why?), and the banner is
 * where he reads which.
 */
const LOCK_NOTICES: Record<string, { titleKey: string; helpKey: string; danger: boolean }> = {
  RETURNED: {
    titleKey: "challan.lockedReturnedTitle",
    helpKey: "challan.lockedReturnedHelp",
    danger: false,
  },
  INVOICED: {
    titleKey: "challan.lockedInvoicedTitle",
    helpKey: "challan.lockedInvoicedHelp",
    danger: false,
  },
  CANCELLED: {
    titleKey: "challan.lockedCancelledTitle",
    helpKey: "challan.lockedCancelledHelp",
    danger: true,
  },
}

/* -------------------------------------------------------------------------- */
/* The screen                                                                  */
/* -------------------------------------------------------------------------- */

export function ChallanDetailScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()
  const formatDay = useFormatDay()
  const queryClient = useQueryClient()

  const query = useChallan(id)
  const challan = query.data
  const pdf = useChallanPdf(id, challan?.number)

  const [sharing, setSharing] = useState(false)
  /*
   * SHARING A CHALLAN NAMES THE LORRY, NEVER A RUPEE FIGURE — and that is the one
   * place this differs from `share.messageInvoice` rather than mirroring it.
   *
   * A challan's `grandTotalPaise` is the DECLARED VALUE OF GOODS IN TRANSIT. Every
   * other surface in this feature refuses to present it as money owed:
   * `DeclaredValueCard` never says "grand total" and carries `challan.taxNotPayable`
   * under the tax rows, because the other party may be a galvaniser this business
   * is PAYING, on material that was never sold at all. A WhatsApp message reading
   * "Challan CHL/26-27/7 — ₹7,965.00", with none of those captions travelling
   * beside it, would assert exactly the liability the whole file is written to
   * avoid asserting. The vehicle number is the fact the recipient actually needs,
   * and it is the one field on the paper he can verify against what is at his gate.
   */
  const onShare = async () => {
    if (challan === undefined || id === undefined) return
    setSharing(true)
    try {
      const filename = `${challan.number.replace(/\//g, "-")}.pdf`
      const fetched = await requestBlob(endpoints.challanPdf(id), { fallbackFilename: filename })
      const outcome = await shareDocument({
        title: t("share.titleChallan", { number: challan.number }),
        text:
          challan.vehicleNumber === null || challan.vehicleNumber === ""
            ? t("share.messageChallan", { number: challan.number })
            : t("share.messageChallanVehicle", {
                number: challan.number,
                vehicle: challan.vehicleNumber,
              }),
        file: { blob: fetched.blob, filename: fetched.filename ?? filename },
        phone: challan.customer?.phone ?? null,
      })
      if (outcome.status === "fallback") toast.info(t("share.openedWhatsapp"))
      if (outcome.status === "failed") toast.error(t("share.noShareTarget"))
      // "shared" was its own confirmation; "dismissed" is him changing his mind.
    } catch {
      toast.error(t("share.couldNotPrepare"))
    } finally {
      setSharing(false)
    }
  }

  if (query.isPending) return <DetailSkeleton />

  if (query.isError) {
    /*
     * A 404 HERE IS AMBIGUOUS BY DESIGN, AND THIS BRANCH MUST NOT RESOLVE IT.
     * Every challan route sits behind `requireFlag("challans")`, which runs
     * BEFORE `requireCap` and answers 404 rather than 403 precisely so that
     * "forbidden" cannot leak that unreleased work exists (§18.3). So a 404 means
     * either "no such challan" or "the feature is off", and `ApiError.isNotFound`
     * deliberately cannot tell them apart. It says NOT FOUND and never "you do
     * not have permission" — the latter is the very leak the 404 was chosen to
     * avoid. There is no `useFlag` on the client and this screen does not invent
     * one.
     */
    const notFound = query.error instanceof ApiError && query.error.status === 404
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink />
        <Alert variant="destructive">
          <AlertTitle>{notFound ? t("err.notFound") : t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            {notFound ? null : (
              <Button
                variant="outline"
                onClick={() => {
                  void query.refetch()
                }}
              >
                {t("btn.retry")}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (challan === undefined) return <DetailSkeleton />

  const notice = LOCK_NOTICES[challan.status]
  /*
   * MARKING THE GOODS BACK — OPEN, RETURNABLE, AND `challan.edit`.
   *
   * `challan.edit` rather than a capability of its own: recording what happened
   * to a document you may already revise wholesale is not a separate permission
   * anybody would ever grant differently, and §2.1's point is that the map stays
   * readable. The server refuses everything else with a 409 written for the
   * owner — goods never marked returnable cannot come back, a RETURNED challan is
   * closed, an INVOICED one is closed by a tax document, a CANCELLED one has
   * nothing to bring back — and this only decides whether to OFFER the button.
   */
  const canMarkReturned =
    can("challan.edit") && challan.status === "OPEN" && challan.isReturnable

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight tabular-nums">{challan.number}</h1>
            <DocumentStatusBadge status={challan.status} kind="challan" />
          </div>
          {/*
            * THE OTHER PARTY AND THE DATE, WITH NO LABEL ON THE NAME. On an
            * INWARD challan this name is the SENDER, not the buyer, so any word
            * bolted in front of it here would be wrong half the time. Who is who
            * is stated once, properly, in the parties card below.
            */}
          <p className="text-muted-foreground text-sm">
            {challan.customer === null || challan.customer.name === ""
              ? t("lbl.customerUnknown")
              : challan.customer.name}{" "}
            · {formatDay(challan.date)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SyncStatus
            updatedAt={query.dataUpdatedAt}
            syncing={query.isFetching}
            onSync={() => {
              void query.refetch()
            }}
          />

          {/*
            * THE PRIMARY ACTION, AND THE ONLY DARK BUTTON. Opening the challan is
            * what he came here to do; there is no Convert on this screen to
            * compete with it, so unlike the quotation's View it never steps down
            * to an outline.
            *
            * NO `?copy=` — the route's own default prints ORIGINAL, and a
            * challan has no `copy_type` column for it to fall back to. Sending
            * `ORIGINAL` explicitly from here would be a second copy of that rule.
            * Rule 55(2) makes the DUPLICATE the copy the driver must carry, but
            * it is asked for BY NAME in the menu beside this, not made the
            * default of a button labelled only "View the challan".
            *
            * UNGATED, exactly as the bill's is. Reading a challan is
            * `challan.view`, which every role holds — the gate is on the API and
            * a second one here would only be able to disagree with it. Only
            * authoring actions are gated on this screen.
            */}
          <Button
            onClick={() => {
              pdf.mutate(undefined)
            }}
            disabled={pdf.isPending}
          >
            <FileTextIcon />
            {pdf.isPending ? t("msg.loading") : t("btn.viewChallan")}
          </Button>

          {/*
            * THE THREE COPIES, AND THIS MENU IS THE REASON THE WHOLE ROW EXISTS.
            * Rule 55(2) requires the challan in triplicate and says who holds
            * each; the DUPLICATE is the one the DRIVER must be carrying. The
            * server has rendered all three since the route landed, with the
            * §17.7 captions — the only thing missing was a way to ask for one.
            *
            * A NAVIGATION MENU, NOT A MENUBAR, and `gap-2` on the list: both are
            * fixes recorded on `InvoiceDetailScreen`, where a menubar drew the
            * two triggers inside one bordered pill and made two independent menus
            * read as a single segmented control.
            */}
          <NavigationMenu>
            <NavigationMenuList className="gap-2">
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className={TRIGGER_AS_BUTTON}
                  disabled={pdf.isPending}
                  data-testid="challan-copies-menu"
                >
                  <PrinterIcon className="size-4" />
                  {t("doc.copiesMenu")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="flex w-56 flex-col">
                    {CHALLAN_COPIES.map((copy) => (
                      <li key={copy}>
                        {/*
                          * `render` because these ACT rather than navigate — they
                          * fetch a PDF. A bare NavigationMenuLink emits an anchor
                          * with no href, which is neither a button nor a link to
                          * anything.
                          */}
                        <NavigationMenuLink
                          render={
                            <button
                              type="button"
                              data-testid={`challan-copy-${copy}`}
                              className="w-full text-left"
                              onClick={() => {
                                pdf.mutate(copy)
                              }}
                            />
                          }
                        >
                          {t(COPY_LABEL_KEY[copy])}
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={TRIGGER_AS_BUTTON} disabled={sharing}>
                  <Share2Icon className="size-4" />
                  {sharing ? t("share.preparing") : t("btn.share")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="flex w-56 flex-col">
                    <li>
                      <NavigationMenuLink
                        render={
                          <button
                            type="button"
                            data-testid="challan-share-whatsapp"
                            className="w-full text-left"
                            onClick={() => {
                              void onShare()
                            }}
                          />
                        }
                      >
                        <Share2Icon />
                        {t("btn.shareWhatsapp")}
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink
                        render={
                          <button
                            type="button"
                            className="w-full text-left"
                            onClick={() => {
                              pdf.mutate(undefined)
                            }}
                          />
                        }
                      >
                        <DownloadIcon />
                        {t("btn.downloadPdf")}
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/*
            * AND NO "MAKE A COPY", WHICH THE BILL HAS HERE. `routes/paths.ts`
            * declares no `challanCopy` and says why: every movement is its own
            * event, with its own date, its own lorry and its own reason, so a
            * copied challan would carry last week's vehicle number onto today's
            * gate — and the vehicle number is the one field on the paper the
            * person checking it can actually verify. `ChallanEditorScreen` reads
            * no `?copyFrom=` for the same reason. There is no route to link to,
            * so this is an absence rather than a hidden control.
            *
            * EDIT — HIDDEN, NOT DISABLED, once the movement closes. `isEditable`
            * is the SERVER's own answer (OPEN only), and it asserts the same rule
            * again on the `PUT`, so this flag hides a route and never authorises
            * one. A challan IS editable while open, unlike an issued invoice, and
            * that is not an inconsistency: an invoice is a tax document corrected
            * by a credit note (§17.3), whereas a challan records a physical
            * movement and the paper has to match what actually went on the lorry.
            */}
          {can("challan.edit") && challan.isEditable ? (
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={routes.challanEdit(challan.id)} />}
            >
              <PencilIcon />
              {t("btn.edit")}
            </Button>
          ) : null}
        </div>
      </header>

      {/*
        * THE STATE OF THE MOVEMENT, SAID FIRST — and on a cancelled challan, the
        * REASON, because this document is the only place the owner will ever be
        * shown it (`audit_log` is behind `audit.view`, which he does not hold).
        */}
      {notice === undefined ? null : (
        <Alert variant={notice.danger ? "destructive" : "default"}>
          {notice.danger ? <TriangleAlertIcon /> : <InfoIcon />}
          <AlertTitle>{t(notice.titleKey)}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            <span>{t(notice.helpKey, { number: challan.number })}</span>

            {challan.status === "CANCELLED" && challan.cancelReason !== null ? (
              <span>{t("doc.cancelledReason", { reason: challan.cancelReason })}</span>
            ) : null}

            {/*
              * THE BILL IT BECAME, AS A LINK. `linkedInvoiceId` is the ONE field
              * on a challan behind which a real row exists; "billed" with no way
              * to reach the bill sends the owner searching the register for a
              * number nobody has told him.
              */}
            {challan.linkedInvoiceId === null ? null : (
              <Button
                variant="outline"
                nativeButton={false}
                render={<Link to={routes.invoice(challan.linkedInvoiceId)} />}
              >
                <FileTextIcon />
                {t("challan.openInvoice")}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/*
        * THE SERVER'S OWN SENTENCE WHERE THERE IS ONE. `doc.pdfFailedHelp` is the
        * generic fallback, not the first choice: a 404 from `requireFlag("challans")`
        * and a 500 from the renderer are different problems and the API words them
        * differently.
        */}
      {pdf.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("doc.pdfFailedTitle")}</AlertTitle>
          <AlertDescription>
            {pdf.error instanceof ApiError ? pdf.error.message : t("doc.pdfFailedHelp")}
          </AlertDescription>
        </Alert>
      ) : null}

      {/*
        * THE SHEET ITSELF, once bytes have arrived. Without this the View button
        * would fetch a PDF that goes nowhere — the object URL is state on the
        * hook, not a navigation.
        */}
      {pdf.url === null ? null : <PdfPanel url={pdf.url} filename={pdf.filename} />}

      {/* The one lifecycle act a human performs here (§17.7). */}
      {canMarkReturned ? <MarkReturnedCard challan={challan} /> : null}

      <PartiesCard challan={challan} />

      <MovementFacts challan={challan} />

      <Card className="overflow-hidden py-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>{t("lbl.description")}</TableHead>
                <TableHead>HSN</TableHead>
                <TableHead className="text-right">{t("lbl.quantity")}</TableHead>
                <TableHead className="text-right">{t("lbl.rate")}</TableHead>
                <TableHead className="text-right">{t("lbl.taxRate")}</TableHead>
                <TableHead className="text-right">{t("lbl.amount")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {challan.lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                    {t("doc.noLines")}
                  </TableCell>
                </TableRow>
              ) : (
                challan.lines.map((line, index) => (
                  <LineRow key={line.id} line={line} index={index} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <DeclaredValueCard challan={challan} />

      {challan.notes === null && challan.terms === null ? null : (
        <Card>
          <CardHeader>
            <CardTitle>{t("challan.wordsTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            {challan.notes === null ? null : (
              <div>
                <div className="text-muted-foreground text-xs">{t("lbl.notes")}</div>
                <p className="whitespace-pre-wrap">{challan.notes}</p>
              </div>
            )}
            {challan.terms === null ? null : (
              <div>
                <div className="text-muted-foreground text-xs">{t("lbl.terms")}</div>
                <p className="whitespace-pre-wrap">{challan.terms}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Last, and alone. See CANCELLABLE. */}
      {can("challan.cancel") && CANCELLABLE.has(challan.status) ? (
        <CancelSection
          challan={challan}
          onCancelled={() => {
            // The register shows status, and a cancelled challan drops out of it
            // unless asked for by name; the home screen counts unbilled ones.
            void queryClient.invalidateQueries()
          }}
        />
      ) : null}
    </div>
  )
}

function BackLink(): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <Link
      to={routes.challans}
      className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon className="size-4" />
      {t("nav.challans")}
    </Link>
  )
}

/**
 * The challan itself — inline on a big screen, a download on a phone.
 *
 * `<object>` RATHER THAN `<iframe>`: it degrades to its children when the
 * platform has no inline PDF viewer, which is every mobile browser. The owner is
 * on a phone, so the download link in the header is the REAL path for him and not
 * a fallback (§11.1). Identical to the bill's and the quotation's, down to the
 * `h-[70vh]`, because three panels that behave differently would be three things
 * to learn.
 */
function PdfPanel({ url, filename }: { url: string; filename: string | null }): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Card className="overflow-hidden py-0" data-testid="challan-pdf-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-3 py-4">
        <CardTitle>{t("doc.pdfTitleChallan")}</CardTitle>
        <Button
          variant="outline"
          nativeButton={false}
          render={<a href={url} download={filename ?? "challan.pdf"} />}
        >
          <DownloadIcon />
          {t("btn.downloadPdf")}
        </Button>
      </CardHeader>
      <Separator />
      <object data={url} type="application/pdf" className="hidden h-[70vh] w-full md:block">
        <p className="p-6 text-sm">{t("doc.pdfHelp")}</p>
      </object>
      <p className="text-muted-foreground p-4 text-sm md:hidden">{t("doc.pdfHelp")}</p>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* Who sent, who received — Rule 55(1)                                         */
/* -------------------------------------------------------------------------- */

/**
 * The two parties, in the order the goods travelled.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE DIRECTION COMES OFF THE WIRE. `challan.direction` is derived SERVER-side
 * from `challanType` and is deliberately not a stored column, so that a stored
 * copy can never disagree with the type. The rule for this screen follows from
 * the same argument: do not recompute it here either. `direction === "INWARD"`
 * is the whole of the logic, and it is read once.
 *
 * INWARD happens on `JOB_WORK_IN` alone — the customer's own material arriving
 * for fabrication or repair. Then THE CUSTOMER IS THE CONSIGNER and this business
 * is the consignee. Printing it the other way round names the receiver as the
 * sender of goods that are already on a lorry, which is a false document under
 * Rule 55(1) rather than a cosmetic slip.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * THE CUSTOMER'S NAME IS A LINK TO THEIR RECORD, asked for by name. It shows the
 * FROZEN snapshot (§4) and links to the LIVE row, which are different things:
 * you read what the challan recorded and click through to correct what the next
 * one will record.
 *
 * OUR OWN BLOCK COMES FROM `GET /company`, which needs only authentication — not
 * `settings.write` — so the OWNER sees it too. While it is loading or if it
 * fails, the block still names the role and falls back to a neutral label rather
 * than leaving an unlabelled empty box where a party should be.
 */
function PartiesCard({ challan }: { challan: ChallanDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const company = useCompany()

  const inward = challan.direction === "INWARD"
  const us = company.data

  const ourBlock = (
    <PartyBlock
      testId={inward ? "challan-consignee" : "challan-consigner"}
      role={inward ? t("challan.consignee") : t("challan.consigner")}
      name={us?.tradeName ?? us?.legalName ?? t("challan.thisBusiness")}
      gstin={us?.gstin ?? null}
      addressLines={
        us === undefined
          ? []
          : [us.addressLine1, us.addressLine2, us.city, us.pincode, us.stateName]
      }
    />
  )

  const theirBlock = (
    <PartyBlock
      testId={inward ? "challan-consigner" : "challan-consignee"}
      role={inward ? t("challan.consigner") : t("challan.consignee")}
      name={
        challan.customer === null || challan.customer.name === ""
          ? t("lbl.customerUnknown")
          : challan.customer.name
      }
      /* THE OWNER ASKED FOR THIS: the other party's name opens their record. */
      to={routes.customer(challan.customerId)}
      gstin={challan.customer?.gstin ?? null}
      addressLines={addressOf(challan.customer)}
    />
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("challan.partiesTitle")}</CardTitle>
        <CardDescription>{t("challan.partiesHelp")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
        {/*
          * SENDER FIRST, RECEIVER SECOND, ALWAYS — so the pair reads as the
          * journey rather than as two boxes whose order the reader has to work
          * out. On an inward challan that puts the CUSTOMER on the left, which
          * is the visible consequence of the swap and the point of it.
          */}
        {inward ? theirBlock : ourBlock}
        <div className="flex items-center justify-center sm:px-2">
          <ArrowRightIcon aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
        </div>
        {inward ? ourBlock : theirBlock}
      </CardContent>
    </Card>
  )
}

function addressOf(customer: DocumentCustomerSnapshot | null): (string | null)[] {
  if (customer === null) return []
  return [
    customer.addressLine1,
    customer.addressLine2,
    customer.city,
    customer.pincode,
    customer.stateName,
  ]
}

/**
 * One party on the challan: what they are called and what their role is.
 *
 * `data-testid` IS HERE ON PURPOSE AND SHOULD STAY. The consigner/consignee swap
 * is the one property of this screen that is a legal defect when wrong, so its
 * test has to pin the ROLE a block plays rather than the wording of a label —
 * wording is translated, reworded and owned by the locale files, and a test that
 * breaks when someone improves a sentence is a test that gets deleted.
 */
function PartyBlock({
  testId,
  role,
  name,
  to,
  gstin,
  addressLines,
}: {
  testId: string
  role: string
  name: string
  to?: string
  gstin: string | null
  addressLines: (string | null)[]
}): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <address data-testid={testId} className="flex-1 text-sm not-italic">
      <div className="text-muted-foreground text-xs tracking-wide uppercase">{role}</div>
      <div className="font-medium">
        {to === undefined ? (
          name
        ) : (
          <Link to={to} className="hover:text-primary underline-offset-4 hover:underline">
            {name}
          </Link>
        )}
      </div>
      {addressLines
        .filter((part) => part !== null && part !== "")
        .map((part, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: address lines have no id.
          <div key={index} className="text-muted-foreground">
            {part}
          </div>
        ))}
      {gstin === null || gstin === "" ? null : (
        <div className="text-muted-foreground tabular-nums">
          {t("lbl.gstin")}: {gstin}
        </div>
      )}
    </address>
  )
}

/* -------------------------------------------------------------------------- */
/* The movement itself                                                         */
/* -------------------------------------------------------------------------- */

function MovementFacts({ challan }: { challan: ChallanDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const customer = challan.customer

  /*
   * A FACT MAY CARRY A DESTINATION, and `to` is set ONLY where a real row exists
   * behind it — never inferred from typed text. On a challan that is exactly one
   * field: `linkedInvoiceId`. The lorry number and the ship-to address are free
   * text the owner typed, and the invoice's own `challanNumber` field (the
   * mirror of this relationship, on the other screen) holds the CUSTOMER'S
   * reference and is explicitly not ours to link.
   */
  const facts: { label: string; value: string | null; to?: string }[] = [
    { label: t("challan.typeLabel"), value: t(TYPE_LABEL_KEY[challan.challanType]) },
    {
      label: t("challan.directionLabel"),
      // THE SERVER'S ANSWER, rendered. Never `challanDirection(challan.challanType)`.
      value:
        challan.direction === "INWARD"
          ? t("challan.directionInward")
          : t("challan.directionOutward"),
    },
    { label: t("challan.vehicleLabel"), value: challan.vehicleNumber },
    /*
     * BLANK SHIP-TO MEANS "the address printed above", which is the ordinary
     * case. The `.filter` below drops the row entirely rather than inventing a
     * placeholder — nothing the owner typed is ever decorated or stood in for.
     */
    { label: t("challan.shipToLabel"), value: challan.shipToAddress },
    {
      label: t("challan.comesBackLabel"),
      value: challan.isReturnable ? t("challan.comesBackYes") : t("challan.comesBackNo"),
    },
    {
      label: t("challan.expectedBackLabel"),
      // A CALENDAR DAY (`YYYY-MM-DD`) — straight to `formatDay`.
      value: challan.expectedReturnDate === null ? null : formatDay(challan.expectedReturnDate),
    },
    {
      label: t("challan.returnedOnLabel"),
      /*
       * AN ISO INSTANT, NOT A CALENDAR DAY — so it goes through
       * `istDayOfInstant` first. Slicing an instant to ten characters takes its
       * UTC day and prints it as an IST one, and anything recorded between 00:00
       * and 05:30 IST would report yesterday. The owner enters Saturday's return
       * on Monday evening; the date has to be the one he means.
       */
      value: challan.returnedAt === null ? null : formatDay(istDayOfInstant(challan.returnedAt)),
    },
    {
      label: t("challan.linkedInvoiceLabel"),
      /*
       * THE SAME WORDS AS THE BANNER'S BUTTON, on purpose. The challan response
       * carries the invoice's ID and not its NUMBER, so there is no reference to
       * print here — and inventing one, or printing the raw id, would put a
       * figure on screen that appears on no document the owner holds.
       */
      value: challan.linkedInvoiceId === null ? null : t("challan.openInvoice"),
      to: challan.linkedInvoiceId === null ? undefined : routes.invoice(challan.linkedInvoiceId),
    },
    { label: t("doc.placeOfSupply"), value: challan.placeOfSupply },
    {
      label: t("doc.taxType"),
      // The server decided this (§5); rendering it from the two state codes here
      // would be a second implementation of `determineSupplyType`.
      value: challan.supplyType === "INTER_STATE" ? t("doc.taxInter") : t("doc.taxIntra"),
    },
    { label: t("lbl.contactName"), value: challan.attnName },
    /*
     * The other party's PAN, exactly as the printed document derives it (§9.2):
     * registered → characters 3-12 of the GSTIN, unregistered → the Rule 114B PAN
     * typed on the customer. The snapshot makes the two mutually exclusive, so
     * this `??` chain can only produce one. `panFromGstin` is the SHARED helper,
     * so screen and paper cannot disagree.
     */
    { label: t("lbl.pan"), value: panFromGstin(customer?.gstin) ?? customer?.pan ?? null },
  ].filter((fact) => fact.value !== null && fact.value !== "")

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("challan.movementTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {facts.map((fact) => (
            <div key={fact.label} className="flex justify-between gap-4 border-b pb-1">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="text-right font-medium">
                {fact.to === undefined ? (
                  fact.value
                ) : (
                  <Link
                    to={fact.to}
                    className="hover:text-primary underline-offset-4 hover:underline"
                  >
                    {fact.value}
                  </Link>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

function LineRow({ line, index }: { line: DocumentLine; index: number }): React.JSX.Element {
  const { t } = useTranslation()

  if (line.isGroupHeader) {
    return (
      <TableRow className="bg-muted/40">
        <TableCell className="tabular-nums">{line.lineCode ?? index + 1}</TableCell>
        <TableCell colSpan={5} className="font-medium">
          {line.description}
        </TableCell>
        <TableCell className="text-muted-foreground text-right text-xs">
          {t("a11y.headerAmount")}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <TableRow>
      <TableCell className="text-muted-foreground tabular-nums">
        {line.lineCode ?? index + 1}
      </TableCell>
      <TableCell className={line.parentLineId === null ? "" : "pl-8"}>{line.description}</TableCell>
      <TableCell className="tabular-nums">{line.hsn}</TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap">
        {/* MILLI-UNITS → units, for DISPLAY only. Rule 55(1) permits a
          * provisional quantity where the exact one is not yet known; whatever
          * was stored is what prints. */}
        {(line.qtyMilli / 1000).toFixed(3).replace(/\.?0+$/, "")} {line.unitLabel}
      </TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap">
        {formatRupees(line.ratePaise)}
      </TableCell>
      {/* BASIS POINTS → percent, for display. `1800` is 18%. */}
      <TableCell className="text-right tabular-nums">{line.gstRateBp / 100}%</TableCell>
      <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
        {formatRupees(line.lineTotalPaise)}
      </TableCell>
    </TableRow>
  )
}

/**
 * The money — and the sentence that stops it being read as a bill.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NOTHING HERE IS OUTPUT TAX. Rule 55(1) requires the taxable value on the
 * challan, and the rate and amount where the movement is for supply, so the lines
 * run through the same engine an invoice's do and the server sends real CGST /
 * SGST / IGST figures. None of it reaches GSTR-3B and none of it is money anyone
 * pays: only the SERIAL RANGE is declared, in GSTR-1 (§17.4).
 *
 * So the bottom line is labelled DECLARED VALUE OF THE GOODS, not "grand total",
 * and the tax rows carry the caption that they are shown rather than owed. A
 * screen that labels these figures "tax payable" or "amount due" asserts a
 * liability that does not exist — against a customer who may be a galvaniser this
 * business is PAYING, on material that was never sold at all.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function DeclaredValueCard({ challan }: { challan: ChallanDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const totals = challan.totals
  // The server decided the supply type (§5); this only renders its consequence.
  const interState = totals.igstPaise > 0

  const taxRows: { label: string; paise: number; show: boolean }[] = [
    { label: "CGST", paise: totals.cgstPaise, show: !interState },
    { label: "SGST", paise: totals.sgstPaise, show: !interState },
    { label: "IGST", paise: totals.igstPaise, show: interState },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("challan.valueTitle")}</CardTitle>
        <CardDescription>{t("challan.notATaxInvoiceHelp")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <dl className="ml-auto flex w-full max-w-sm flex-col gap-2 text-sm">
          <div className="flex justify-between gap-6">
            <dt className="text-muted-foreground">{t("challan.goodsValue")}</dt>
            <dd className="tabular-nums">{formatRupees(totals.subtotalPaise)}</dd>
          </div>

          {totals.totalTaxPaise === 0 ? null : (
            <>
              {taxRows
                .filter((row) => row.show)
                .map((row) => (
                  <div key={row.label} className="flex justify-between gap-6">
                    <dt className="text-muted-foreground">{row.label}</dt>
                    <dd className="tabular-nums">{formatRupees(row.paise)}</dd>
                  </div>
                ))}
              {/*
                * ONE CAPTION FOR THE GROUP, not a repeat on each row. Three
                * copies of "not payable" reads as insistence and pushes the
                * figures out of alignment; one line under them says the same
                * thing once, where the eye lands after reading the amounts.
                */}
              <div className="text-muted-foreground text-xs">{t("challan.taxNotPayable")}</div>
            </>
          )}

          {totals.roundOffPaise === 0 ? null : (
            <div className="flex justify-between gap-6">
              <dt className="text-muted-foreground">{t("lbl.roundOff")}</dt>
              <dd className="tabular-nums">{formatRupees(totals.roundOffPaise)}</dd>
            </div>
          )}

          <Separator />

          <div className="flex justify-between gap-6 text-base font-semibold">
            {/* NEVER `lbl.grandTotal` — see the note above this component. */}
            <dt>{t("challan.declaredValue")}</dt>
            <dd className="tabular-nums">{formatRupees(totals.grandTotalPaise)}</dd>
          </div>
        </dl>

        {totals.amountInWords === "" ? null : (
          <div className="mt-2 border-t pt-3 text-sm">
            <span className="text-muted-foreground">{t("doc.amountInWords")}: </span>
            {/*
              * DERIVED BY THE SERVER, not stored — `delivery_challans` has no
              * such column, because the figure is a declared value in transit
              * and not a sum anyone pays. Worded there so this page and any
              * future printed challan cannot word the same number differently.
              */}
            <span className="font-medium">{totals.amountInWords}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* "The goods came back"                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Closing an open, returnable challan (§17.7).
 *
 * THE DATE IS ASKED FOR, AND THAT IS THE WHOLE POINT OF THE DIALOG. The lorry
 * comes back on Saturday and the entry is made on Monday — `returnedAt` omitted
 * means "now", which would put Monday on a movement that closed on Saturday and
 * quietly make the overdue list wrong for two days. Blank is still allowed,
 * because "they came back just now" is the other ordinary case, and the note
 * under the field says which is which.
 *
 * ONLY `RETURNED` IS REACHABLE FROM THIS SCREEN. `INVOICED` is set by raising the
 * invoice; claiming it by hand would mark a delivery billed with no bill behind
 * it, which is exactly the blindness §17.7 exists to remove.
 */
function MarkReturnedCard({ challan }: { challan: ChallanDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [returnedAt, setReturnedAt] = useState("")
  const markReturned = useMarkChallanReturned(String(challan.id))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("challan.returnTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3">
        <p className="text-muted-foreground text-sm">{t("challan.returnHelp")}</p>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger
            data-testid="challan-mark-returned"
            render={<Button variant="outline" />}
          >
            <PackageCheckIcon />
            {t("challan.returnAction")}
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("challan.returnConfirmTitle", { number: challan.number })}
              </AlertDialogTitle>
              <AlertDialogDescription>{t("challan.returnConfirmHelp")}</AlertDialogDescription>
            </AlertDialogHeader>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="challan-returned-at">{t("challan.returnedOnLabel")}</Label>
              <DateField
                id="challan-returned-at"
                value={returnedAt}
                onChange={setReturnedAt}
                clearable
                placeholder={t("challan.returnedTodayPlaceholder")}
              />
              <p className="text-muted-foreground text-xs">{t("challan.returnDateHelp")}</p>
            </div>

            {markReturned.isError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {/*
                    * THE SERVER'S OWN SENTENCE, not a generic failure. It refuses
                    * with a 409 written for the owner — "these goods were not
                    * expected back", "already billed, raise a credit note" — and
                    * that text is the only thing on screen that explains why a
                    * button he could see did not work.
                    */}
                  {markReturned.error instanceof ApiError
                    ? markReturned.error.message
                    : t("err.somethingWentWrong")}
                </AlertDescription>
              </Alert>
            ) : null}

            <AlertDialogFooter>
              <AlertDialogCancel render={<Button variant="outline" />}>
                {t("btn.cancel")}
              </AlertDialogCancel>
              <Button
                disabled={markReturned.isPending}
                onClick={() => {
                  markReturned.mutate(
                    // Blank means "now", which is the server's default — an
                    // empty string is not a date and must never reach the wire.
                    { returnedAt: returnedAt === "" ? undefined : returnedAt },
                    {
                      onSuccess: () => {
                        setOpen(false)
                        toast.success(t("challan.returnDone", { number: challan.number }))
                        /*
                         * Everything: the register shows status, `overdueOnly`
                         * stops matching a returned challan however late it was,
                         * and the home screen counts what is still out.
                         */
                        void queryClient.invalidateQueries()
                      },
                    },
                  )
                }}
              >
                {markReturned.isPending ? t("msg.saving") : t("challan.returnConfirmAction")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* Cancelling                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Cancelling the movement. Last, alone, and terminal.
 *
 * A STATUS, NEVER A DELETE, AND THE NUMBER STAYS CONSUMED. Challan serial ranges
 * are declared in GSTR-1 (§17.4), so a missing `CHL/26-27/7` is a question from
 * the department — the same reasoning that makes an invoice cancellable but never
 * deletable (§5).
 *
 * THE REASON IS MANDATORY, and it is written to the `cancel_reason` COLUMN as
 * well as to the audit row. `audit_log` is an ADMIN surface (`audit.view`, §2.1)
 * the owner cannot open; the challan itself is the only place he will ever be
 * shown why this one was cancelled, which is why the banner above prints it back.
 *
 * THE CLIENT GUARD IS THREE CHARACTERS AND THE API'S IS ONE. `ChallanCancelSchema`
 * is `.min(1).max(500)`, unlike the quotation void and the invoice cancel, which
 * are 3–500. Guarding at three here is safe because it is STRICTER than the API —
 * but nothing in this file may claim the server enforces three, because it does
 * not. `maxLength={500}` is real on all three.
 */
function CancelSection({
  challan,
  onCancelled,
}: {
  challan: ChallanDetail
  onCancelled: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const cancel = useCancelChallan(String(challan.id))

  const tooShort = reason.trim().length < 3

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive flex items-center gap-2 text-base">
          <OctagonAlertIcon aria-hidden="true" className="size-4 shrink-0" />
          {t("cancelChallan.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3">
        <p className="text-muted-foreground text-sm">{t("cancelChallan.help")}</p>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger
            data-testid="challan-cancel"
            render={
              <Button
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
              />
            }
          >
            <OctagonAlertIcon />
            {t("cancelChallan.action")}
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("cancelChallan.confirmTitle", { number: challan.number })}
              </AlertDialogTitle>
              <AlertDialogDescription>{t("cancelChallan.confirmHelp")}</AlertDialogDescription>
            </AlertDialogHeader>

            <ul className="text-muted-foreground flex list-disc flex-col gap-1.5 pl-5 text-sm">
              <li>{t("cancelChallan.keepsNumber", { number: challan.number })}</li>
              <li>{t("cancelChallan.staysInList")}</li>
              <li>{t("cancelChallan.cannotUndo")}</li>
            </ul>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="challan-cancel-reason">{t("cancelChallan.reasonLabel")}</Label>
              <Textarea
                id="challan-cancel-reason"
                data-testid="challan-cancel-reason"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
                maxLength={500}
                rows={3}
              />
              <p className="text-muted-foreground text-xs">{t("cancelChallan.reasonHelp")}</p>
            </div>

            {cancel.isError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {cancel.error instanceof ApiError
                    ? cancel.error.message
                    : t("err.somethingWentWrong")}
                </AlertDescription>
              </Alert>
            ) : null}

            <AlertDialogFooter>
              <AlertDialogCancel render={<Button variant="outline" />}>
                {t("cancelChallan.keepIt")}
              </AlertDialogCancel>
              <Button
                data-testid="challan-cancel-confirm"
                variant="destructive"
                disabled={tooShort || cancel.isPending}
                onClick={() => {
                  cancel.mutate(reason.trim(), {
                    onSuccess: () => {
                      setOpen(false)
                      toast.success(t("cancelChallan.done", { number: challan.number }))
                      onCancelled()
                    },
                  })
                }}
              >
                {t("cancelChallan.confirmAction")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

function DetailSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  )
}
